/**
 * ч.958 (рішення власника): «Oblik+/AI» — баланс кредитів і ІСТОРІЯ руху:
 * дата · вид дії · сума. Види уніфіковані людською мовою на бекенді
 * (поповнення / розпізнавання виписки / аудит проводок / розпізнавання
 * первинки) — фронт лише фарбує знак.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

interface CreditRow {
  id: string;
  date: string;
  kind: 'TOPUP' | 'CHARGE' | 'HOLD' | 'RELEASE';
  action: string;
  credits: string;
  note: string | null;
}

const fmt = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AiCreditsSection() {
  const balanceQ = useQuery({
    queryKey: ['ai-balance'],
    queryFn: () => api.get<{ balance: string; held?: string; available?: string; unlimited?: boolean;
      unlimited_reason?: string | null }>(
      '/ai/balance').then((r) => r.data),
  });
  const histQ = useQuery({
    queryKey: ['ai-credit-history'],
    queryFn: () => api.get<{ items: CreditRow[]; total: number }>(
      '/ai/credit-history', { params: { limit: 200 } }).then((r) => r.data),
  });

  const cols: ColumnDef<CreditRow>[] = [
    {
      key: 'date', header: 'Дата', width: 160,
      sortValue: (r) => r.date,
      cell: (r) => new Date(r.date).toLocaleString('uk-UA'),
    },
    {
      key: 'action', header: 'Вид дії',
      sortValue: (r) => r.action,
      cell: (r) => (
        <>
          {r.action}
          {r.note && <span style={{ color: 'var(--muted)' }}> · {r.note}</span>}
        </>
      ),
    },
    {
      key: 'credits', header: 'Кредити', width: 140, align: 'right',
      sortValue: (r) => Number(r.credits),
      cell: (r) => {
        const income = r.kind === 'TOPUP' || r.kind === 'RELEASE';
        return (
          <span style={{ color: income ? '#15803d' : '#b91c1c', fontWeight: 600 }}>
            {income ? '+' : '−'}{fmt(r.credits)}
          </span>
        );
      },
    },
  ];

  return (
    <>
      <div className="card">
        <div className="toolbar" style={{ alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.05rem' }}>Oblik+/AI</h2>
            <div style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
              Кредити списуються за фактично виконану роботу помічника.
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>Баланс</div>
            <div style={{ fontSize: '1.6rem', fontWeight: 700 }}>
              {balanceQ.isLoading ? '…' : fmt(balanceQ.data?.balance ?? 0)}
            </div>
          </div>
          {balanceQ.data?.available !== undefined && (
            <div>
              <div style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>Доступно зараз</div>
              <div style={{ fontSize: '1.6rem', fontWeight: 700 }} data-testid="ai-available">
                {balanceQ.data.unlimited ? 'без обмежень' : fmt(balanceQ.data.available)}
              </div>
            </div>
          )}
          {balanceQ.data?.held !== undefined && Number(balanceQ.data.held) > 0 && (
            <div>
              <div style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>Зарезервовано</div>
              <div style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--badge-amber-fg)' }}>
                {fmt(balanceQ.data.held)}
              </div>
            </div>
          )}
        </div>
        {balanceQ.data?.unlimited && (
          <div data-testid="ai-unlimited-note"
            style={{ marginTop: '0.75rem', color: 'var(--muted)', fontSize: '0.85rem' }}>
            {balanceQ.data.unlimited_reason === 'platform_owner_user'
              ? 'Ви власник платформи: ваші виклики Oblik+/AI кредити цього акаунта не списують.'
              : balanceQ.data.unlimited_reason === 'platform_owner'
                ? 'Акаунт власника платформи: Oblik+/AI працює без обмежень, кредити не списуються.'
                : 'Тестовий акаунт: Oblik+/AI працює без обмежень, кредити не списуються.'}
          </div>
        )}
      </div>

      <div className="card">
        <div className="toolbar">
          <h2 style={{ margin: 0, fontSize: '1.05rem' }}>Історія кредитів</h2>
        </div>
        <DataTable<CreditRow>
          rows={histQ.data?.items ?? []}
          columns={cols}
          rowKey={(r) => r.id}
          loading={histQ.isLoading}
          emptyText="Операцій з кредитами ще не було."
          initialSortKey="date"
          initialSortDir="desc"
        />
      </div>
    </>
  );
}
